/**
 * Sidebar Component
 *
 * @package
 */

import { useState, useEffect, useMemo } from '@wordpress/element';
import apiFetch from '@wordpress/api-fetch';
import { __ } from '@wordpress/i18n';
import BaseModelCard from './BaseModelCard';
import BundleCard from './BundleCard';
import ProductAccordion from './ProductAccordion';
import SidebarAccordion from './SidebarAccordion';
import VehicleTitle from './VehicleTitle';
import { countGroupOptions } from '../helpers/groupOptions';

const Sidebar = ( {
	config,
	selectedBaseModel,
	setSelectedBaseModel,
	selectedProducts,
	setSelectedProducts,
	selectedBundle,
	setSelectedBundle,
	vatMode,
	quantities,
	setProductQuantity,
	workshopHourlyRate,
	taxRate,
} ) => {
	const [ bundles, setBundles ] = useState( [] );
	const [ lockedProducts, setLockedProducts ] = useState( [] );

	const preconfiguredBundleIdsKey = useMemo(
		() => JSON.stringify( config.preconfiguredBundleIds ?? [] ),
		[ config.preconfiguredBundleIds ]
	);

	useEffect( () => {
		const ids = Array.isArray( config.preconfiguredBundleIds )
			? config.preconfiguredBundleIds.map( id => parseInt( id, 10 ) ).filter( id => id > 0 )
			: [];

		if ( ! config.vehicleConfigId || ids.length === 0 ) {
			setBundles( [] );
			return;
		}

		const allowed = new Set( ids );

		apiFetch( {
			path: `/at-configurator/v1/bundles?vehicle_config=${ config.vehicleConfigId }&per_page=100`,
		} )
			.then( bundlesData => {
				const list = Array.isArray( bundlesData ) ? bundlesData : [];
				setBundles( list.filter( b => allowed.has( parseInt( b.id, 10 ) ) ) );
			} )
			.catch( () => {
				setBundles( [] );
			} );
	}, [ config.vehicleConfigId, preconfiguredBundleIdsKey ] );

	useEffect( () => {
		if ( ! selectedBundle ) {
			setLockedProducts( [] );
			return;
		}

		const bundle = bundles.find( b => b.id === selectedBundle );
		if ( bundle?.acf?.selected_optionals && Array.isArray( bundle.acf.selected_optionals ) ) {
			setLockedProducts( bundle.acf.selected_optionals.map( id => parseInt( id, 10 ) ) );
		} else {
			setLockedProducts( [] );
		}
	}, [ selectedBundle, bundles ] );

	// Keyed on the original index so groups don't remount as siblings appear or disappear.
	const populatedProductGroups = ( config.productGroups || [] )
		.map( ( group, index ) => ( { group, index } ) )
		.filter( ( { group } ) => countGroupOptions( group ) > 0 );

	return (
		<aside className="configurator-sidebar" aria-label={ __( 'Configuration options', 'at-vehicle-configurator' ) }>
			<div className="configurator-sidebar-header">
				<div className="configurator-sidebar-subheading">
					{ __( 'Design Your Arctic Truck', 'at-vehicle-configurator' ) }
				</div>
				<VehicleTitle
					vehicleConfigId={ config?.vehicleConfigId }
					className="configurator-sidebar-title"
				/>
			</div>
			{ ( ( config.baseModels && config.baseModels.length > 0 ) || config.allowNoBaseModel ) && (
				<SidebarAccordion
					title={ __( 'Base Models', 'at-vehicle-configurator' ) }
					id="base-models-accordion"
					defaultOpen={ config.sectionSettings?.baseModels?.defaultOpen !== false }
					description={ config.sectionSettings?.baseModels?.description }
					readMoreUrl={ config.sectionSettings?.baseModels?.readMoreUrl }
				>
					<div className="configurator-base-models">
						{ config.baseModels &&
							config.baseModels.map( ( baseModel, index ) => {
								const productId = typeof baseModel === 'object' ? baseModel.product_id : baseModel;
								return (
									<BaseModelCard
										key={ productId || index }
										modelId={ productId }
										description={ typeof baseModel === 'object' ? baseModel.description : null }
										image={ typeof baseModel === 'object' ? baseModel.image : null }
										isSelected={ selectedBaseModel === productId }
										onSelect={ () => setSelectedBaseModel( productId ) }
										vatMode={ vatMode }
									/>
								);
							} ) }
						{ config.allowNoBaseModel && (
							<button
								type="button"
								className={ `configurator-base-model-card configurator-base-model-card--no-base ${ selectedBaseModel === 0 ? 'selected' : '' }` }
								onClick={ () => setSelectedBaseModel( 0 ) }
								aria-pressed={ selectedBaseModel === 0 }
							>
								<div className="configurator-base-model-card-content">
									<div className="configurator-base-model-card-header">
										<h3>{ __( 'No base model', 'at-vehicle-configurator' ) }</h3>
									</div>
									<p className="configurator-base-model-card-excerpt">
										{ __( 'Configure options only. Base model price is excluded from totals and financing.', 'at-vehicle-configurator' ) }
									</p>
								</div>
							</button>
						) }
					</div>
				</SidebarAccordion>
			) }

			{ bundles.length > 0 && (
				<SidebarAccordion
					title={ __( 'Pre-configured Bundles', 'at-vehicle-configurator' ) }
					id="bundles-accordion"
					defaultOpen={ config.sectionSettings?.bundles?.defaultOpen !== false }
					description={ config.sectionSettings?.bundles?.description }
					readMoreUrl={ config.sectionSettings?.bundles?.readMoreUrl }
				>
					<div className="configurator-bundles">
						{ bundles.map( bundle => (
							<BundleCard
								key={ bundle.id }
								bundleData={ bundle }
								config={ config }
								isSelected={ selectedBundle === bundle.id }
								onSelect={ () => setSelectedBundle( bundle.id ) }
								onDeselect={ productIds => {
									setSelectedBundle( null );
									if ( productIds && productIds.length > 0 ) {
										setSelectedProducts( prevProducts => {
											const productIdsToRemove = productIds.map( id => parseInt( id, 10 ) );
											return prevProducts.filter( productId => ! productIdsToRemove.includes( parseInt( productId, 10 ) ) );
										} );
									}
								} }
								onAutoSelect={ ( baseModel, products ) => {
									if ( baseModel ) {
										setSelectedBaseModel( baseModel );
									}
									if ( products && products.length > 0 ) {
										setSelectedProducts( products );
									}
								} }
							/>
						) ) }
					</div>
				</SidebarAccordion>
			) }

			{ populatedProductGroups.length > 0 && (
				<SidebarAccordion
					title={ __( 'Options', 'at-vehicle-configurator' ) }
					id="options-accordion"
					defaultOpen={ config.sectionSettings?.options?.defaultOpen !== false }
					description={ config.sectionSettings?.options?.description }
					readMoreUrl={ config.sectionSettings?.options?.readMoreUrl }
					className="configurator-sidebar-accordion--plain"
				>
					<div className="configurator-product-groups">
						{ populatedProductGroups.map( ( { group, index } ) => (
							<ProductAccordion
								key={ index }
								group={ group }
								selectedProducts={ selectedProducts }
								setSelectedProducts={ setSelectedProducts }
								lockedProducts={ lockedProducts }
								vatMode={ vatMode }
								quantities={ quantities }
								setProductQuantity={ setProductQuantity }
								workshopHourlyRate={ workshopHourlyRate }
								taxRate={ taxRate }
							/>
						) ) }
					</div>
				</SidebarAccordion>
			) }

		</aside>
	);
};

export default Sidebar;
